import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiAuthError, fetchMonthlySeries } from '../api/client';
import { SignInScreen } from '../auth/SignInScreen';
import { useToken } from '../auth/TokenContext';
import { MonthlyCostByModelChart } from '../components/charts/MonthlyCostByModelChart';
import { MonthlyCostByOriginChart } from '../components/charts/MonthlyCostByOriginChart';
import { MonthlyUsersSessionsChart } from '../components/charts/MonthlyUsersSessionsChart';
import { KpiCard } from '../components/KpiCard';

const MONTHS = 12;
const QUERY_KEY = 'monthly-series';

function formatUsd(n: number): string {
  if (n >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(0)}%`;
}

export function Monthly(): JSX.Element {
  const { token, signOut } = useToken();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tokenKey = token?.slice(-8);
  const handleMonthClick = (month: string): void => navigate(`/monthly/${month}`);

  const monthlyQuery = useQuery({
    queryKey: [QUERY_KEY, MONTHS, tokenKey],
    queryFn: () => fetchMonthlySeries(token, MONTHS),
    enabled: !!token,
  });

  if (monthlyQuery.error instanceof ApiAuthError) {
    const err = monthlyQuery.error;
    return (
      <SignInScreen
        reason={`Token rejected by the worker (HTTP ${err.status}): ${err.message}`}
      />
    );
  }

  const data = monthlyQuery.data;
  const points = data?.points ?? [];
  const current = data?.current;
  const deltas = data?.deltas;
  const currentMonth = points[points.length - 1]?.month ?? '';
  const previousMonth = points[points.length - 2]?.month ?? '';

  return (
    <section className="overview">
      <div className="overview-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Last {MONTHS} months</h2>
          {current && (
            <p className="chart-subtitle" style={{ margin: '4px 0 0' }}>
              Current month: <strong>{currentMonth}</strong> (MTD) vs. previous:{' '}
              <strong>{previousMonth || '—'}</strong>
            </p>
          )}
        </div>
        <button
          type="button"
          className="refresh-button"
          disabled={monthlyQuery.isFetching}
          onClick={() => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })}
          title="Refresh now"
        >
          {monthlyQuery.isFetching ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      {monthlyQuery.isError && !(monthlyQuery.error instanceof ApiAuthError) && (
        <div className="overview-error">
          Failed to load monthly data.{' '}
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      )}

      <div className="kpi-strip">
        <KpiCard
          label="Total cost"
          value={current ? formatUsd(current.totalCostUsd) : ''}
          delta={deltas?.totalCostUsd ?? null}
          invertColors
          loading={monthlyQuery.isLoading}
          hint="USD across all models this month"
        />
        <KpiCard
          label="Active users"
          value={current ? formatNumber(current.activeUsers) : ''}
          delta={deltas?.activeUsers ?? null}
          loading={monthlyQuery.isLoading}
          hint="Distinct user_id in the month"
        />
        <KpiCard
          label="Sessions"
          value={current ? formatNumber(current.sessions) : ''}
          delta={deltas?.sessions ?? null}
          loading={monthlyQuery.isLoading}
          hint="Cone-aggregated sessions started this month"
        />
        <KpiCard
          label="Completion rate"
          value={current ? formatPercent(current.completionRate) : ''}
          delta={deltas?.completionRate ?? null}
          loading={monthlyQuery.isLoading}
          hint="v5 outcome=completed / classified sessions"
        />
        <KpiCard
          label="Cost / active user"
          value={current ? formatUsd(current.costPerActiveUserUsd) : ''}
          delta={deltas?.costPerActiveUserUsd ?? null}
          invertColors
          loading={monthlyQuery.isLoading}
          hint="totalCost / activeUsers — efficiency lens"
        />
        <KpiCard
          label="Cost / completed session"
          value={current ? formatUsd(current.costPerCompletedSessionUsd) : ''}
          delta={deltas?.costPerCompletedSessionUsd ?? null}
          invertColors
          loading={monthlyQuery.isLoading}
          hint="totalCost / completedSessions"
        />
      </div>

      <section className="chart-section">
        <header>
          <h2>Cost by month — by model</h2>
          <p className="chart-subtitle">
            Stacked USD cost per month, split by model (Opus / Sonnet / Haiku). Model
            mix drives cost trends more than raw volume — Opus runs ~5× Sonnet. Current
            month is shown as month-to-date.
          </p>
        </header>
        <MonthlyCostByModelChart
          points={points}
          loading={monthlyQuery.isLoading}
          onMonthClick={handleMonthClick}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Cost by month — Adobe vs. non-Adobe</h2>
          <p className="chart-subtitle">
            Stacked USD cost per month split by user origin (Adobe employee email vs.
            other IMS tenants). Always shows both regardless of the global origin
            filter — that's the point of this breakdown.
          </p>
        </header>
        <MonthlyCostByOriginChart
          points={points}
          loading={monthlyQuery.isLoading}
          onMonthClick={handleMonthClick}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Adoption — active users &amp; sessions by month</h2>
          <p className="chart-subtitle">
            Active users (indigo, left axis) and cone-aggregated sessions (emerald,
            right axis) per month. Two axes because typical sessions-per-user ratios
            (5–30) would crush the users bar on a shared scale.
          </p>
        </header>
        <MonthlyUsersSessionsChart
          points={points}
          loading={monthlyQuery.isLoading}
          onMonthClick={handleMonthClick}
        />
      </section>
    </section>
  );
}
